import React from 'react';
import DefaultDataTable from '../../../../components/default/DefaultDataTable';
import { SalesOrderDetail, SalesOrderDetailFilters } from '../../../../app/models/SalesOrderDetail';
import ButtonTableAction from '../../../../components/buttons/ButtonTableAction';
import { formatMoney } from '../../../../utils/utils';

interface Props {
    salesOrderDetails: SalesOrderDetail[];
    totalRows: number;
    loading?: boolean;
    edit?: (id: number) => void;
    destroy?: (id: number) => void;
    filter: SalesOrderDetailFilters;
    setFilter: (filter: SalesOrderDetailFilters) => void;
    callbackSuccess: () => void;
    onSearch: () => void;
}

const SalesOrderDetailDataTable = ({
    salesOrderDetails,
    totalRows,
    loading = false,
    edit,
    destroy,
    filter,
    setFilter,
    callbackSuccess,
    onSearch
}: Props) => {
    const columns = [
        {
            name: 'Pos',
            selector: (row: SalesOrderDetail) => row.position || 0,
            sortable: true,
            sortField: 'position',
            width: '80px',
            cell: (row: SalesOrderDetail) => (
                <span>{row.position || 0}</span>
            )
        },
        {
            name: 'Producto',
            selector: (row: SalesOrderDetail) => row.product || '',
            sortable: true,
            sortField: 'product',
            minWidth: '200px'
        },
        {
            name: 'Cantidad',
            selector: (row: SalesOrderDetail) => row.quantity || 0,
            sortable: true,
            sortField: 'quantity',
            width: '120px',
            cell: (row: SalesOrderDetail) => (
                <span>
                    {row.quantity} {row.quantity_unit || ''}
                </span>
            )
        },
        {
            name: 'Precio',
            selector: (row: SalesOrderDetail) => row.price || 0,
            sortable: true,
            sortField: 'price',
            width: '110px',
            cell: (row: SalesOrderDetail) => (
                <span>{formatMoney(row.price || 0)}</span>
            )
        },
        {
            name: 'Moneda Precio',
            selector: (row: SalesOrderDetail) => row.price_currency_iso || '',
            sortable: false,
            width: '110px',
            cell: (row: SalesOrderDetail) => (
                <span>{row.price_currency_iso || ''}</span>
            )
        },
        {
            name: 'Unidad Precio',
            selector: (row: SalesOrderDetail) => row.price_unit || '',
            sortable: false,
            width: '120px',
            cell: (row: SalesOrderDetail) => (
                <span>{row.price_unit || ''}</span>
            )
        },
        {
            name: 'Total',
            selector: (row: SalesOrderDetail) => (row.quantity || 0) * (row.price || 0),
            sortable: false,
            width: '120px',
            cell: (row: SalesOrderDetail) => {
                const total = (row.quantity || 0) * (row.price || 0);
                return <span>{formatMoney(total)}</span>;
            }
        },
        {
            name: 'Moneda Total',
            selector: (row: SalesOrderDetail) => row.price_currency_iso || '',
            sortable: false,
            width: '110px',
            cell: (row: SalesOrderDetail) => (
                <span>{row.price_currency_iso || ''}</span>
            )
        },
        {
            name: 'Fecha de Pedido',
            selector: (row: SalesOrderDetail) => row.order_date || '',
            sortable: true,
            sortField: 'order_date',
            width: '150px',
            cell: (row: SalesOrderDetail) => {
                if (!row.order_date) return <span></span>;
                
                // Parsear la fecha sin conversión de timezone
                // Si viene como string "YYYY-MM-DD", parsearlo directamente
                let dateStr = row.order_date;
                if (typeof dateStr === 'string' && dateStr.includes('T')) {
                    // Si tiene hora, tomar solo la parte de la fecha
                    dateStr = dateStr.split('T')[0];
                }
                
                // Parsear YYYY-MM-DD a formato DD/MM/YYYY
                const parts = dateStr.split('-');
                if (parts.length === 3) {
                    const [year, month, day] = parts;
                    return <span>{day}/{month}/{year}</span>;
                }
                
                // Fallback: intentar con Date pero ajustando para evitar problemas de timezone
                try {
                    const date = new Date(dateStr + 'T12:00:00'); // Agregar mediodía para evitar problemas de timezone
                    const day = String(date.getDate()).padStart(2, '0');
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const year = date.getFullYear();
                    return <span>{day}/{month}/{year}</span>;
                } catch {
                    return <span>{dateStr}</span>;
                }
            }
        },
        {
            name: 'Acciones',
            selector: (row: SalesOrderDetail) => row?.id,
            sortable: false,
            width: '150px',
            cell: (row: SalesOrderDetail) => (
                <div className="">
                    {edit && (
                        <ButtonTableAction
                            callbackFunction={() => edit(row?.id ?? -1)}
                            classIcon={'mdi mdi-pencil'}
                            colorIcon={'text-warning'}
                            errorMessage={'No se puede editar este registro.'}
                            title={'Editar'}
                        />
                    )}
                    {destroy && (
                        <ButtonTableAction
                            callbackFunction={() => destroy(row?.id ?? -1)}
                            classIcon={'mdi mdi-delete'}
                            colorIcon={'text-danger'}
                            errorMessage={'No se puede eliminar este registro.'}
                            title={'Eliminar'}
                        />
                    )}
                </div>
            )
        }
    ];

    return (
        <DefaultDataTable
            columns={columns}
            data={salesOrderDetails}
            progressPending={loading}
            paginationTotalRows={totalRows}
            filter={filter}
            setFilter={setFilter}
        />
    );
};

export default SalesOrderDetailDataTable;

